<template>
  <div>
    <div class="portlet">
      <div class="portlet-head">
        <h3><i class="fas fa-boxes"></i> Cennik ({{ rows }})</h3>
        <div>
          <button class="btn btn-outline-secondary" @click="refresh">
            <i class="fas fa-sync"></i> Odśwież
          </button>
          <b-button
            variant="outline-primary"
            v-b-modal.products-category-modal
          >
            <i class="fas fa-tools"></i> Zarządzaj kategoriami
          </b-button>
          <button class="btn btn-outline-primary" @click="showAddProductModal">
            <i class="fas fa-plus"></i> Dodaj produkt
          </button>
        </div>
      </div>
      <div class="portlet-body">
        <div class="searchpanel">
          <form
            class="searching mb-3 searchpanel-item"
            @submit.prevent="doSearch"
          >
            <div class="input-group flex-nowrap">
              <div class="searching-wrapper searchpanel-item">
                <input
                  type="text"
                  class="form-control"
                  placeholder="Szukaj"
                  aria-label="Szukaj"
                  v-model="search"
                />
                <span class="searching-icon">
                  <i class="fas fa-search"></i>
                </span>
                <span
                  class="searching-clean"
                  v-show="search"
                  @click="cleanSearch"
                >
                  <i class="fas fa-times"></i>
                </span>
              </div>
              <div class="input-group-append">
                <button
                  class="btn btn-sm btn-outline-secondary d-none"
                  type="submit"
                >
                  Szukaj
                </button>
              </div>
            </div>
          </form>
          <div class="filters d-flex">
              <multiselect
                id="categories"
                class="searchpanel-item"
                placeholder="Filtruj po kategorii"
                label="name"
                track-by="name"
                :options="options"
                :multiple="true"
                :max="8"
                :show-labels="false"
                :group-select="false"
                style="min-width: 300px"
                v-model="selectedCategories"
              >
                <template slot="option" slot-scope="props">
                  <span v-html="props.option.html"></span>
                </template>
              </multiselect>
          </div>
        </div>

        <b-table
          class="table table-list table-pro priceList-table"
          :fields="fields"
          :per-page="perPage"
          :current-page="currentPage"
          :busy="isBusy"
          :items="filteredProducts"
          stacked="lg"
          show-empty
          empty-text="Brak produktów"
        >
          <template v-slot:table-busy>
            <TableSpiner />
          </template>
          <template v-slot:empty="{ emptyText }">
            <div class="empty">
              <i class="fas fa-box-open empty-state"></i>
              <span class="empty-search">{{ emptyText }}</span>
            </div>
          </template>
          <template v-slot:cell(name)="data">
            <div>{{data.item.name}}</div>
            <small><span>{{(searchTree(root, data.item.category_id).slice(1)).join("/")}}</span></small>
          </template>
          <template v-slot:cell(description)="data">
            <span v-if="data.item.description === 'undefined'">-</span>
            <span v-else>{{data.item.description}}</span>
          </template>
          <template v-slot:cell(unit)="data">
            <span v-if="data.item.unit === 'undefined'">-</span>
            <span v-else>{{data.item.unit}}</span>
          </template>
          <template v-slot:cell(netto_amount)="data">
            {{data.item.netto_amount.toFixed(2)}}
          </template>
          <template v-slot:cell(options)="data">
            <b-dropdown no-caret right variant="light" class="btn-rounded">
              <template v-slot:button-content>
                <i class="fas fa-ellipsis-v"></i>
              </template>
              <b-dropdown-item-button @click="showEditProductModal(data.item)"
                >Edytuj</b-dropdown-item-button
              >
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item-button
                class="danger"
                @click="showRemoveProductModal(data.item)"
              >
                <i class="fas fa-trash-alt"></i> Usuń
              </b-dropdown-item-button>
            </b-dropdown>
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          v-if="rows > this.perPage"
          :total-rows="rows"
          :per-page="perPage"
        ></b-pagination>
      </div>
    </div>
    <ProductsCategoryModalForm />
    <ModalProducts ref="productsModal" />
    <RemoveProductModal ref="removeProduct" @ok="removeProduct" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import TableSpiner from "@/components/TableSpiner";
import ProductsCategoryModalForm from "@/components/products/ProductsCategoryModalForm";
import ModalProducts from "@/components/products/ModalProducts";
import RemoveProductModal from "@/components/products/RemoveProductModal.vue";
import { flatenCategories } from "@/utils/index.js";

export default {
  name: "ThePriceList",
  components: {
    TableSpiner,
    ModalProducts,
    RemoveProductModal,
    ProductsCategoryModalForm
  },
  data() {
    return {
      search: "",
      isBusy: false,
      perPage: 25,
      currentPage: 1,
      exporting: false,
      filter: null,
      selectedPosition: null,
      selected: {},
      selectedCategories: [],
      fields: [
        { key: "name", label: "Nazwa", class: "" },
        { key: "description", label: "Opis", class: "" },
        { key: "unit", label: "Jednostka", class: "text-center" },
        { key: "netto_amount", label: "Cena [EUR]", class: "text-right" },
        { key: "vat_rate", label: "VAT %", class: "text-center" },
        { key: "options", label: "Opcje", class: "text-center" },
      ],
    };
  },
  computed: {
    ...mapGetters("products", ["products", "root"]),
    filteredProducts() {
      let filteredProducts = this.products;
      if (this.selectedCategories.length) {
        filteredProducts = this.products.reduce((prev, curr) => {
          if (
            this.selectedCategories.find(
              ({ value }) => value === curr.category_id
            )
          ) {
            prev.push(curr);
          }
          return prev;
        }, []);
      }
      return filteredProducts.filter(({ name }) =>
        name.toLowerCase().includes(this.search.toLowerCase())
      );
    },
    options() {
      const [, ...rest] = flatenCategories(this.root, 0);
      return [...rest];
    },
    flatenCategories() {
      return flatenCategories(this.root, 0);
    },
    rows() {
      return this.products.length;
    },
  },
  methods: {
    ...mapActions("products", ["get", "remove", "getCategories"]),
    showAddProductModal(){
      this.$bvModal.show("products-modal");
    },
    doSearch() {},
    cleanSearch() {
      this.search = "";
    },
    refresh() {
      this.isBusy = true;
      this.$Progress.start();
      this.get()
        .then(() => {
          this.isBusy = false;
        })
        .catch(() => {
          this.isBusy = false;
          return [];
        });
    },
    showRemoveProductModal(item) {
      this.$refs.removeProduct.show(item);
    },
    showEditProductModal(item) {
      this.$refs.productsModal.show(item);
    },
    removeProduct(item) {
      this.$Progress.start();
      const modal = this.$refs.removeProduct;
      this.remove(item.id)
        .then(() => {
          modal.hide();
        })
        .catch((error) => {
          modal.error(error);
        });
    },
    getCategoryName(category_id) {
      let category = this.flatenCategories().find(
        ({ value }) => value === category_id
      );
      if (!category) return "";
      return category.originalElement.name;
    },

    searchTree(element, categoryId) {
      if (element.id == categoryId) {
          return [element.name];
      }
      if (!element.children) {
          return null;
      }
      var childrens = null;
      for (var i = 0, n = element.children.length; i < n; i++) {
          childrens = this.searchTree(element.children[i], categoryId);
          if (childrens) {
              childrens.unshift(element.name);
              i = n + 1;
          }
      }
      return childrens;
    }
  },
  mounted() {
    this.isBusy = true;
    Promise.all([this.get(), this.getCategories()]).then(() => {
      this.isBusy = false;
    });
  },
};
</script>


